import React, { useEffect } from 'react';
import { orderBy } from 'lodash-es';
import { gql } from '@soundxyz/gql-string';
import type { Maybe } from '@soundxyz/utils';
import { FeedListSkeleton } from '@components/Feed/ListViews/components/Skeleton';
import { UserHoverCardContent } from '@components/Feed/User/UserHoverCardContent';
import { renderItemContent } from '@components/Song/Activity/ReleaseActivityFeed';
import { ChartCard } from '@components/basics/ChartCard';
import { EmptySection } from '@components/basics/ChartCard/AccordionDetails';
import { EXPANDED_MOBILE_AVATAR_AMOUNT } from '@components/basics/ChartCard/Collectors';
import { useChartCardContext } from '@components/basics/ChartCard/Context';
import { Avatar } from '@components/basics/Images/Avatar';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { UserHoverCard } from '@components/main-components/user/UserHoverCard';
import { MediaQuery } from '@context/mediaQuery';
import { ToastContext } from '@context/toast';
import { ChartCardDetailsDocument, useQuery } from '@graphql/reactQuery';
import { trackClickEvent } from '@lib/analytics';
import { CLIENT_QUERY_REDUCTION_ON_FRESH_SSR_STRATEGY } from '@utils/constants';
import { webappUriPath } from '@utils/url';

/*#__PURE__*/ gql(/* GraphQL */ `
  query ChartCardDetails($input: ReleaseChartRankInput!, $collectorAmount: NonNegativeInt!) {
    releaseChartRank(input: $input) {
      id
      currentRank
      rankLast
      rankPeak
      trendingStreak
      release {
        id
        webappUri
        collectors(pagination: { first: $collectorAmount }) {
          edges {
            node {
              id
              user {
                id
                avatar {
                  id
                  url
                }
                username
                publicAddress
                webappUri
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
        artist {
          id
          webappUri
        }
        activityFeed(input: { pagination: { first: 3 } }) {
          edges {
            node {
              __typename
              id
              date
              user {
                id
              }
              ... on PrimarySale {
                id
                ...ReleaseFeedPrimarySale
              }
              ... on BondingCurveSale {
                id
                ...ReleaseFeedBondingCurveSale
              }
              ... on BondingCurveSold {
                id
                ...ReleaseFeedBondingCurveSold
              }
              ... on SecondarySale {
                id
                purchasedFromUser {
                  id
                }
                ...ReleaseFeedSecondarySale
              }

              ... on ReleasesAddedToPlaylist {
                id
                playlist {
                  id
                }
                ...ReleaseFeedReleasesAdded
              }
              ... on ReleasesAddedRemovedFromPlaylist {
                id
                numAddedReleases
                numRemovedReleases
                playlist {
                  id
                  ...PlaylistActivityInfo
                }
                user {
                  id
                  ...MinimalUser
                }
              }
              ... on PlaylistCreated {
                id
                date
                playlist {
                  id
                  name
                  ...PlaylistActivityInfo
                }
                user {
                  ...MinimalUser
                }
              }
              ... on ReleaseDropped {
                id
                user {
                  id
                }
                ...ReleaseFeedReleaseDropped
              }
              ... on ReleaseLiked {
                id
                user {
                  id
                }
                release {
                  id
                }
                ...ReleaseFeedReleaseLiked
              }
            }
          }
        }
      }
    }
  }
`);

export function ChartDetails({
  chartRankId,
  artistName,
  trackTitle,
}: {
  chartRankId: Maybe<string>;
  artistName?: string | null;
  trackTitle?: string;
}) {
  const { openToast } = ToastContext.useContainer();
  const { tablet } = MediaQuery.useContainer();
  const size = tablet ? 'tablet' : 'mobile';
  const { setIsLoadingDetails } = useChartCardContext();
  const { data: chart, isFetching } = useQuery(ChartCardDetailsDocument, {
    variables: !!chartRankId && {
      input: { id: chartRankId },
      collectorAmount: size === 'mobile' ? EXPANDED_MOBILE_AVATAR_AMOUNT : 0,
    },
    ...CLIENT_QUERY_REDUCTION_ON_FRESH_SSR_STRATEGY,
    onError: error => {
      if (error) {
        openToast({
          text: 'Something went wrong. Please try again later.',
          variant: 'error',
        });
        // eslint-disable-next-line no-console
        console.error(error);
      }
    },
  });
  const { rankLast, rankPeak, currentRank, trendingStreak, release } =
    chart?.data.releaseChartRank || {};
  const { webappUri, collectors, activityFeed, artist } = release || {};
  const songUrl = webappUri && webappUriPath(webappUri);
  const artistUrl = artist?.webappUri && webappUriPath(artist?.webappUri);

  useEffect(() => {
    setIsLoadingDetails?.(!!isFetching);
  }, [isFetching, setIsLoadingDetails]);

  return (
    <ChartCard.AccordionDetails
      artistName={
        artistUrl ? (
          <AccessibleLink onClick={e => e.stopPropagation()} pathnameUri={artistUrl}>
            {artistName}
          </AccessibleLink>
        ) : (
          <>{artistName}</>
        )
      }
      trackTitle={
        songUrl ? (
          <AccessibleLink onClick={e => e.stopPropagation()} pathnameUri={songUrl}>
            {trackTitle}
          </AccessibleLink>
        ) : (
          <>{trackTitle}</>
        )
      }
    >
      <section className="md:hidden">
        <ChartCard.AccordionDetails.SectionHeader>
          Top Collectors
        </ChartCard.AccordionDetails.SectionHeader>
        <ChartCard.Collectors
          pathnameUri={webappUriPath(webappUri || '')}
          empty={!collectors?.edges.length && !isFetching}
          collectorAmount={EXPANDED_MOBILE_AVATAR_AMOUNT}
          avatars={
            isFetching ? (
              <ChartCard.AvatarsSkeleton amount={EXPANDED_MOBILE_AVATAR_AMOUNT} size={size} />
            ) : (
              <>
                {collectors?.edges.slice(0, EXPANDED_MOBILE_AVATAR_AMOUNT).map(edge => {
                  const { id: userId, avatar, username, publicAddress, webappUri } = edge.node.user;
                  return (
                    <UserHoverCard
                      key={userId}
                      trigger={
                        <ChartCard.Collectors.AvatarContainer>
                          <ChartCard.Collectors.ImageWrapper loading={isFetching}>
                            {!isFetching && (
                              <Avatar
                                src={avatar?.url}
                                size={
                                  ChartCard.Collectors.CHART_CARD_COLLECTOR_IMAGE_SIZES.expanded[
                                    size
                                  ]
                                }
                                username={username}
                                walletAddress={publicAddress}
                                webappUri={webappUri}
                                borderRadius={0}
                              />
                            )}
                          </ChartCard.Collectors.ImageWrapper>
                        </ChartCard.Collectors.AvatarContainer>
                      }
                      userHoverCardContent={<UserHoverCardContent userId={userId} />}
                    />
                  );
                })}
              </>
            )
          }
        />
      </section>
      <section>
        {/* Activity has a margin top, so we can use that for spacing and skip margin bottom of header to follow design */}
        <ChartCard.AccordionDetails.SectionHeader>
          Recent Activity
        </ChartCard.AccordionDetails.SectionHeader>
        {isFetching || !release ? (
          <FeedListSkeleton rows={3} minimal />
        ) : activityFeed?.edges.length ? (
          orderBy(activityFeed.edges, 'date', 'desc').map((edge, i) =>
            renderItemContent({
              index: i,
              item: edge.node,
              releaseId: release.id,
            }),
          )
        ) : (
          <EmptySection>No recent activity</EmptySection>
        )}
      </section>
      <section>
        <ChartCard.AccordionDetails.SectionHeader>Stats</ChartCard.AccordionDetails.SectionHeader>
        <ChartCard.Stats
          current={currentRank || 0}
          last={rankLast || 0}
          peak={rankPeak || 0}
          streak={trendingStreak || 0}
        />
      </section>
      <div className="md:hidden">
        <ChartCard.Button
          href={webappUriPath(release?.webappUri || '')}
          onClick={() => {
            trackClickEvent({
              event: 'Collect Now Clicked',
              properties: {
                associationId: release?.id || '',
                associationType: 'Release',
                sourceComponent: 'Charts',
              },
            });
          }}
        />
      </div>
    </ChartCard.AccordionDetails>
  );
}
